import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/stores/auth";
import { getActiveWorkspace } from "@/stores/workspace";
import { useTheme } from "@/hooks/useTheme";
import { toggleTheme } from "@/stores/theme";
import FeedbackWidget from "@/components/FeedbackWidget";
import NotificationBell from "@/components/NotificationBell";
import logoSvg from "@/assets/logo.svg";

export default function Layout() {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const ws = getActiveWorkspace();
  const wsQ = ws ? `?workspace=${ws}` : "";
  const [collapsed, setCollapsed] = useState(false);
  const isAdminLike = user?.role === "admin" || user?.role === "super_admin";

  return (
    <div className="app-layout">
      {/* Mobile hamburger — visible only on screens ≤ 1024px via CSS */}
      {collapsed && (
        <button
          className="sidebar-mobile-toggle"
          onClick={() => setCollapsed(false)}
          aria-label={t("nav.home")}
        >
          ☰
        </button>
      )}
      <aside className={`sidebar ${collapsed ? "sidebar-collapsed" : ""}`}>
        <div className="sidebar-brand">
          <img src={logoSvg} alt="ComfyViz" className="brand-logo" />
          {!collapsed && <span className="brand-text">ComfyViz</span>}
        </div>
        <button
          className="sidebar-toggle"
          onClick={() => setCollapsed(!collapsed)}
          title={collapsed ? "▶" : "◀"}
        >
          {collapsed ? "▶" : "◀"}
        </button>
        <nav className="sidebar-nav">
          <NavLink to="/" end title={t("nav.home")}>
            <span className="nav-icon">🏠</span>{!collapsed && <span className="nav-label"> {t("nav.home")}</span>}
          </NavLink>
          <NavLink to="/workspaces" title={t("nav.workspaces")}>
            <span className="nav-icon">📁</span>{!collapsed && <span className="nav-label"> {t("nav.workspaces")}</span>}
          </NavLink>

          <div className="sidebar-divider" />
          <div className="sidebar-section-header">{t("nav.sec_data")}</div>
          <NavLink to={`/pipelines${wsQ}`} title={t("nav.pipelines")}>
            <span className="nav-icon">⚙️</span>{!collapsed && <span className="nav-label"> {t("nav.pipelines")}</span>}
          </NavLink>
          <NavLink to={`/models${wsQ}`} title={t("nav.models")}>
            <span className="nav-icon">🤖</span>{!collapsed && <span className="nav-label"> {t("nav.models")}</span>}
          </NavLink>

          <div className="sidebar-divider" />
          <div className="sidebar-section-header">{t("nav.sec_analytics")}</div>
          <NavLink to={`/reports${wsQ}`} title={t("nav.reports")}>
            <span className="nav-icon">📄</span>{!collapsed && <span className="nav-label"> {t("nav.reports")}</span>}
          </NavLink>
          <NavLink to={`/dashboards${wsQ}`} title={t("nav.dashboards")}>
            <span className="nav-icon">📋</span>{!collapsed && <span className="nav-label"> {t("nav.dashboards")}</span>}
          </NavLink>

          <div className="sidebar-divider" />
          <div className="sidebar-section-header">{t("nav.sec_settings")}</div>
          {isAdminLike && (
            <NavLink to="/security" title={t("nav.security")}>
              <span className="nav-icon">🔒</span>{!collapsed && <span className="nav-label"> {t("nav.security")}</span>}
            </NavLink>
          )}
          {user?.role === "super_admin" && (
            <NavLink to="/admin" title={t("nav.admin")}>
              <span className="nav-icon">🛠️</span>{!collapsed && <span className="nav-label"> {t("nav.admin")}</span>}
            </NavLink>
          )}
          <NavLink to="/subscription" title={t("nav.subscription")}>
            <span className="nav-icon">💳</span>{!collapsed && <span className="nav-label"> {t("nav.subscription")}</span>}
          </NavLink>

          <div className="sidebar-divider" />
          <div className="sidebar-section-header">{t("nav.sec_help")}</div>
          <NavLink to="/faq" title="FAQ">
            <span className="nav-icon">❓</span>{!collapsed && <span className="nav-label"> {t("nav.faq")}</span>}
          </NavLink>
          <NavLink to="/guide" title={t("nav.guide")}>
            <span className="nav-icon">📖</span>{!collapsed && <span className="nav-label"> {t("nav.guide")}</span>}
          </NavLink>
        </nav>
        <div className="sidebar-footer">
          <NotificationBell />
          <button
            className="btn-theme-toggle"
            onClick={toggleTheme}
            title={theme === "dark" ? t("layout.lightMode") : t("layout.darkMode")}
          >
            {theme === "dark" ? "☀️" : "🌙"}
            {!collapsed && <span className="nav-label"> {theme === "dark" ? t("layout.light") : t("layout.dark")}</span>}
          </button>
          {user && (
            <>
              {!collapsed && (
                <button
                  className="user-badge"
                  onClick={() => navigate("/profile")}
                  title={t("nav.profile")}
                >
                  {user.display_name || user.username}
                  <span className="role-chip">{user.role}</span>
                </button>
              )}
              {collapsed && (
                <button
                  className="btn-logout"
                  onClick={() => navigate("/profile")}
                  title={t("nav.profile")}
                >
                  👤
                </button>
              )}
              <button onClick={logout} className="btn-logout" title={t("nav.logout")} data-testid="logout-btn">
                {collapsed ? "🚪" : t("nav.logout")}
              </button>
            </>
          )}
        </div>
      </aside>
      <main className="main-content">
        <Outlet />
      </main>
      <FeedbackWidget />
    </div>
  );
}
